/**
 * 应用主状态管理 (Zustand)
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { Document, Theme } from '../types'
import { storage as indexedDBStorage } from '../utils/indexedDBStorage'

// 新建文档标题
const UNTITLED_DOCUMENT_TITLE = 'Untitled'

interface AppState {
  // 文档状态
  currentDocument: Document | null
  documents: Document[]

  // 桌面端文件路径（Tauri 环境使用）
  currentFilePath: string | null

  // UI 状态
  isSidebarVisible: boolean
  isExportModalOpen: boolean
  theme: Theme

  // 查看器状态
  viewerImage: string | null
  isViewerOpen: boolean
  isFullScreenPreviewOpen: boolean
  isFullWidth: boolean

  // 代码编辑模态框状态（支持 Mermaid 和普通代码）
  codeEditModal: {
    isOpen: boolean
    code: string
    rawMarkdown: string
    language: string
  }

  // 操作方法
  setCurrentDocument: (doc: Document | null) => void
  saveDocument: (content: string, title?: string) => void
  createNewDocument: () => void
  deleteDocument: (id: number) => void
  toggleSidebar: () => void
  toggleExportModal: () => void
  setTheme: (theme: Theme) => void
  openViewer: (imageUrl: string) => void
  closeViewer: () => void
  setFullScreenPreviewOpen: (isOpen: boolean) => void
  toggleFullWidth: () => void
  setCodeEditModal: (state: Partial<AppState['codeEditModal']>) => void
  closeCodeEditModal: () => void
  
  // 桌面端文件操作
  setCurrentFilePath: (path: string | null) => void
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // 初始状态（会被 IndexedDB 覆盖）
      currentDocument: null,
      documents: [],
      currentFilePath: null,
      isSidebarVisible: true,
      isExportModalOpen: false,
      theme: 'auto',
      viewerImage: null,
      isViewerOpen: false,
      isFullScreenPreviewOpen: false,
      isFullWidth: false,
      codeEditModal: {
        isOpen: false,
        code: '',
        rawMarkdown: '',
        language: ''
      },

      // 操作方法
      setCurrentDocument: (doc) => set({ currentDocument: doc }),

      saveDocument: (content, title) => {
        const { currentDocument, documents } = get()
        const now = Date.now()

        if (currentDocument) {
          // 检查当前文档是否已经在列表中
          const exists = documents.some(doc => doc.id === currentDocument.id)
          
          // 更新现有文档内容
          const updatedDoc = {
            ...currentDocument,
            content,
            title: title || currentDocument.title,
            updated: now
          }

          if (!exists) {
            // 如果是临时文档（如欢迎页且 ID 为 -1），转换为正式文档并存入列表
            if (updatedDoc.id === -1) {
              updatedDoc.id = now
            }
            set({
              currentDocument: updatedDoc,
              documents: [updatedDoc, ...documents]
            })
          } else {
            // 正常更新列表中的文档
            set({
              currentDocument: updatedDoc,
              documents: documents.map(doc =>
                doc.id === currentDocument.id ? updatedDoc : doc
              )
            })
          }
        } else {
          // 创建新文档
          const newDoc: Document = {
            id: now,
            title: title || UNTITLED_DOCUMENT_TITLE,
            content,
            created: now,
            updated: now
          }
          set({
            currentDocument: newDoc,
            documents: [...documents, newDoc]
          })
        }
      },

      createNewDocument: () => {
        const now = Date.now()
        const newDoc: Document = {
          id: now,
          title: UNTITLED_DOCUMENT_TITLE,
          content: '',
          created: now,
          updated: now
        }
        set(state => ({
          currentDocument: newDoc,
          documents: [...state.documents, newDoc],
          currentFilePath: null // 清除桌面端文件路径
        }))
      },

      deleteDocument: (id) => {
        set(state => ({
          documents: state.documents.filter(doc => doc.id !== id),
          currentDocument: state.currentDocument?.id === id ? null : state.currentDocument
        }))
      },

      toggleSidebar: () => set(state => ({ isSidebarVisible: !state.isSidebarVisible })),

      toggleExportModal: () => set(state => ({ isExportModalOpen: !state.isExportModalOpen })),

      setTheme: (theme) => set({ theme }),

      openViewer: (imageUrl) => set({ viewerImage: imageUrl, isViewerOpen: true }),
      closeViewer: () => set({ viewerImage: null, isViewerOpen: false }),
      setFullScreenPreviewOpen: (isOpen) => set({ isFullScreenPreviewOpen: isOpen }),
      toggleFullWidth: () => set(state => ({ isFullWidth: !state.isFullWidth })),
      
      setCodeEditModal: (modalState) => set((state) => ({
        codeEditModal: { ...state.codeEditModal, ...modalState }
      })),
      closeCodeEditModal: () => set((state) => ({
        codeEditModal: { ...state.codeEditModal, isOpen: false }
      })),
      
      // 桌面端文件路径管理
      setCurrentFilePath: (path) => set({ currentFilePath: path })
    }),
    {
      name: 'markflow-documents',
      storage: createJSONStorage(() => indexedDBStorage),
      // 只持久化文档数据
      partialize: (state) => ({
        currentDocument: state.currentDocument,
        documents: state.documents
      }),
      // 从 IndexedDB 恢复后处理当前文档
      onRehydrateStorage: () => (state) => {
        if (state) {
          // 如果有文档但没有当前文档，设置为第一个文档
          if (!state.currentDocument && state.documents.length > 0) {
            state.currentDocument = state.documents[0]
          }
        }
      }
    }
  )
)
